import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import SectionTitle from "../components/SectionTitle";
import CtaBanner from "../components/CtaBanner";
import ServicesExcellence from "../components/ServicesExcellence";
import * as Icons from "lucide-react";
import { usePageBanner, useServices } from "../api/queries";
import { preloadPageBannerImage } from "../lib/banners";
import { ArrowRight, Sparkles } from "lucide-react";
import { getServicePreviewText } from "../lib/html";
import { type, typeRichDescription } from "../lib/typography";

function ServiceBlock({ service, index, total }) {
  const Icon = Icons[service.icon] || Icons.Hammer;
  const isEven = index % 2 === 0;
  const serviceNumber = String(index + 1).padStart(2, "0");

  const previewText = useMemo(
    () => getServicePreviewText(service.description, service.title) || service.short || "",
    [service.description, service.title, service.short]
  );

  return (
    <section
      id={`service-${service.id}`}
      className={`scroll-mt-28 relative py-20 md:py-28 overflow-hidden ${isEven ? "bg-white" : "bg-slate-50/80"}`}
    >
      {!isEven && (
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(30,111,207,0.06),_transparent_55%)]" />
      )}
      {isEven && (
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(30,111,207,0.04),_transparent_50%)]" />
      )}

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className={`relative group ${isEven ? "" : "lg:order-2"}`}>
            <span
              className="absolute -top-8 -left-4 text-[7rem] md:text-[9rem] font-black text-slate-100/90 leading-none select-none pointer-events-none z-0"
              aria-hidden="true"
            >
              {serviceNumber}
            </span>

            <Link
              to={`/services/${service.id}`}
              className="relative z-10 block"
              aria-label={`View ${service.title} details`}
              onMouseEnter={() => preloadPageBannerImage(service.bannerImageUrl)}
              onFocus={() => preloadPageBannerImage(service.bannerImageUrl)}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-[#0a1a2f]/15 bg-[#0a1a2f] ring-1 ring-black/5 transition-transform duration-500 group-hover:scale-[1.01]">
                {service.image ? (
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-[280px] md:h-[400px] object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-[280px] md:h-[400px] bg-gradient-to-br from-[#0a1a2f] to-[#1e3a5f]" aria-hidden="true" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1a2f]/75 via-[#0a1a2f]/20 to-transparent" />
                <div className="absolute top-5 left-5 w-14 h-14 rounded-xl bg-[#1e6fcf] flex items-center justify-center shadow-lg shadow-[#1e6fcf]/40 ring-2 ring-white/20">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div className="absolute top-5 right-5 px-3 py-1.5 rounded-full bg-black/30 backdrop-blur-md text-xs font-mono text-white/90 tracking-widest border border-white/10">
                  {serviceNumber}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="inline-flex items-center gap-2 text-white text-sm font-bold uppercase tracking-wider">
                    View details <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>
          </div>

          <div className={isEven ? "" : "lg:order-1"}>
            <div className="relative p-8 md:p-10 rounded-2xl bg-white border border-slate-200/80 shadow-xl shadow-slate-200/50">
              <div className="absolute top-0 left-8 right-8 h-1 rounded-full bg-gradient-to-r from-[#1e6fcf] via-[#aad4fa] to-transparent" />

              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#e8f2fc] ${type.eyebrow} text-[#1758a8]`}>
                <Sparkles className="w-3 h-3" />
                Service {serviceNumber}
              </div>

              <Link to={`/services/${service.id}`} className="block mt-4 group/title"
                onMouseEnter={() => preloadPageBannerImage(service.bannerImageUrl)}
                onFocus={() => preloadPageBannerImage(service.bannerImageUrl)}
              >
                <h2 className={`${type.sectionTitle} text-[#1e6fcf] group-hover/title:text-[#1758a8] transition-colors`}>
                  {service.title}
                </h2>
              </Link>

              {previewText && (
                <p className={`mt-5 ${typeRichDescription} line-clamp-4`}>
                  {previewText}
                </p>
              )}

              <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
                <Link
                  to={`/services/${service.id}`}
                  className="inline-flex items-center justify-center gap-2 bg-[#1e6fcf] hover:bg-[#1758a8] text-white font-bold px-6 py-3.5 rounded-lg shadow-lg shadow-[#1e6fcf]/25 transition-all group text-sm uppercase tracking-wider"
                  onMouseEnter={() => preloadPageBannerImage(service.bannerImageUrl)}
                  onFocus={() => preloadPageBannerImage(service.bannerImageUrl)}
                >
                  View full details
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-[#0a1a2f] font-bold px-6 py-3.5 rounded-lg border-2 border-slate-200 hover:border-[#1e6fcf]/40 transition-all text-sm uppercase tracking-wider"
                >
                  Enquire now
                </Link>
              </div>
            </div>

            {index < total - 1 && (
              <div className="hidden lg:flex items-center gap-3 mt-8 text-slate-300" aria-hidden="true">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
                <span className="text-[10px] font-mono tracking-widest">{serviceNumber}</span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  const { data: page, isLoading, error } = useServices();
  const { banner: serviceBanner, isLoading: bannerLoading } = usePageBanner("service");

  useEffect(() => {
    if (!page) return;
    const hash = window.location.hash;
    if (!hash) return;
    const el = document.getElementById(hash.replace("#", ""));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [page]);

  if (error) {
    return (
      <section className="py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-slate-600">{error.message}</p>
        </div>
      </section>
    );
  }

  if (isLoading || !page) {
    return (
      <section className="py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center text-slate-500">Loading...</div>
      </section>
    );
  }

  const services = page.items;

  return (
    <>
      <PageHeader
        title={page.headerTitle || serviceBanner?.title || "Services"}
        breadcrumb="Services"
        image={serviceBanner?.image}
        isLoading={bannerLoading}
      />

      <section className="py-14 md:py-16 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#1e6fcf]/5 rounded-full blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <SectionTitle
            align="center"
            eyebrow={page.sectionTagline}
            title={page.mainTitle}
            subtitle={page.subText}
          />
        </div>
      </section>

      {services.map((service, index) => (
        <ServiceBlock key={service.id} service={service} index={index} total={services.length} />
      ))}

      <ServicesExcellence />

      <CtaBanner />
    </>
  );
}
